import "./App.css";
import Header from "./components/Header";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import Logo from "./assets/guru.svg";
import Home from "./pages/Home";
import Guides from "./pages/Guides";
import Outcomes from "./pages/Outcomes";
import { NavMenuLink } from "./components/Nav";
import { useEffect, useRef, useState } from "react";
import LanguageSwitcher from "./components/LanguageToggle";
import Customise from "./pages/Customise";
import Benefits from "./pages/Benefits";
import Footer from "./compositions/Footer";
import Carousel from "./compositions/Carousel";
import styles from "./App.module.css";

// MUI COMPONENTS
import "@muibook/components/mui-input";
import "@muibook/components/mui-icons";
import "@muibook/components/mui-body";
import "@muibook/components/mui-stack";
import "@muibook/components/mui-grid";
import "@muibook/components/mui-heading";
import "@muibook/components/mui-button";
import "@muibook/components/mui-list";
import "@muibook/components/mui-link";
import "@muibook/components/mui-image";
import "@muibook/components/mui-drawer";
import "@muibook/components/mui-responsive";
import "@muibook/components/mui-badge";
import "@muibook/components/mui-carousel";
import "@muibook/components/mui-tabs";
import "@muibook/components/mui-code";
import "@muibook/components/mui-card";
import "@muibook/components/mui-rule";
import Advert from "./compositions/Advert";

function App() {
  return (
    <LanguageProvider>
      <Router>
        <MainApp />
      </Router>
    </LanguageProvider>
  );
}

const MainApp: React.FC = () => {
  const { t } = useLanguage();

  // Navigation Hook
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const handleNavMenuToggle = () => {
    setNavMenuOpen(!navMenuOpen);
  };

  // Close the menu on item click
  const handleNavItemClick = () => {
    setNavMenuOpen(false);
  };

  // Sync State:
  // ACCESSING CLOSE, ESC and BACKDROP ACTIONS INSIDE DRAWER
  const menuDrawerRef = useRef<HTMLElement>(null);

  // Menu drawer
  useEffect(() => {
    const drawerEl = menuDrawerRef.current;
    if (!drawerEl) return;

    const handleOpen = () => setNavMenuOpen(true);
    const handleClose = () => setNavMenuOpen(false);

    drawerEl.addEventListener("mui-drawer-open", handleOpen);
    drawerEl.addEventListener("mui-drawer-close", handleClose);

    return () => {
      drawerEl.removeEventListener("mui-drawer-open", handleOpen);
      drawerEl.removeEventListener("mui-drawer-close", handleClose);
    };
  }, [navMenuOpen]);

  // OVERFLOW SCROLL
  useEffect(() => {
    if (navMenuOpen) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }

    // Escape key listener to close nav
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && navMenuOpen) {
        setNavMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navMenuOpen]);

  useEffect(() => {
    const selectors = ["#inert"];
    selectors.forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) {
        if (navMenuOpen) {
          el.setAttribute("inert", "");
        } else {
          el.removeAttribute("inert");
        }
      }
    });

    return () => {
      selectors.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) el.removeAttribute("inert");
      });
    };
  }, [navMenuOpen]);

  const location = useLocation();
  const isHomePage = location.pathname.startsWith("/home");

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      <div id="inert">
        <Header
          logo={
            <a href="/home" className={styles.homeLink}>
              <img src={Logo} alt={t("guru.title")} style={{ width: "28px", height: "28px" }} />
              <mui-h-stack space="var(--space-300)">
                <mui-heading level="1" size="3">
                  {t("guru.title")}
                </mui-heading>
              </mui-h-stack>
            </a>
          }
        >
          <mui-h-stack alignY="center" space="var(--space-000)">
            <LanguageSwitcher variant="tertiary" />
            <mui-button variant="tertiary" onClick={handleNavMenuToggle} aria-label="Menu button">
              <mui-icon-menu></mui-icon-menu>
            </mui-button>
          </mui-h-stack>
        </Header>

        {isHomePage && <Carousel />}

        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<Home />} />
          <Route path="/guides" element={<Guides />}>
            <Route path="customise" element={<Customise />} />
            <Route path="benefits" element={<Benefits />} />
          </Route>
          <Route path="/outcomes" element={<Outcomes />} />
        </Routes>

        <Footer />
      </div>
      {/* NAVIGATION */}
      <mui-drawer
        ref={menuDrawerRef}
        id="menu-drawer"
        variant="overlay"
        width="280px"
        side="right"
        {...(navMenuOpen && { open: true })}
        drawer-space="none"
      >
        <Advert title={t("guides.call-to-action.title")} description={t("guides.call-to-action.description")} />
        <mui-v-stack space="var(--space-200)" class={styles.viewToggle}>
          <NavMenuLink to="/home" onClick={handleNavItemClick}>
            {t("navigation.home")}
          </NavMenuLink>
          <NavMenuLink to="/guides" onClick={handleNavItemClick}>
            {t("navigation.guides")}
          </NavMenuLink>
          <NavMenuLink to="/outcomes" onClick={handleNavItemClick}>
            {t("navigation.outcomes")}
          </NavMenuLink>
        </mui-v-stack>
      </mui-drawer>
    </>
  );
};

export default App;
